import { useEffect, useState } from "react";
import { httpService } from "../../base/services/httpService.service";
import { OrganizationLevel } from "../../base/services/accountLevel.service";
import { ANNUAL_BILLING_TERMS, describePlanPrice } from "../../pages/auth/register/planPricing";
import { formatEnterprisePrice, isEnterprisePacket } from "./enterprise";

// The packets of both levels. A billing status names its packet by id alone, and
// an organization's level is not always settled yet, so both lists are asked for
// and the packet is looked up in whichever has it. getPackets is an account
// endpoint, which the 402 gate leaves reachable.
const fetchAllPackets = () =>
   Promise.all(
      [OrganizationLevel.Organization, OrganizationLevel.Business].map((level) =>
         httpService
            .get(`/Account/getPackets?organizationLevel=${level}`)
            .then((res) => res?.data ?? [])
            .catch(() => [])
      )
   ).then((lists) => lists.flat());

/**
 * How the plan a card is being saved for is named in the agreement: "the
 * Standard plan ($99/month)", or "the Standard plan ($71/month, billed monthly,
 * 12-month commitment)". Priced the way its card is (planPricing.js), which is
 * the plan's list price — tax is added on top and said so in the text. Null
 * until it is known, and when it cannot be.
 *
 * Enterprise is in no packet list, and its price is the organization's own:
 * it is named from `enterpriseTerms` (enterprise.js, enterpriseTermsBilled).
 */
export function usePlanLabel(packetId, isMonthly, enterpriseTerms = null) {
   const [label, setLabel] = useState(null);
   const isEnterprise = isEnterprisePacket(packetId);

   useEffect(() => {
      setLabel(null);
      if (!packetId || isEnterprise) return undefined;

      let isCurrent = true;

      fetchAllPackets().then((packets) => {
         const plan = packets.find((packet) => packet.id === packetId);
         if (!isCurrent || !plan) return;

         setLabel(`the ${plan.name} plan (${describePlanPrice(plan, isMonthly === false)})`);
      });

      return () => {
         isCurrent = false;
      };
   }, [packetId, isMonthly, isEnterprise]);

   if (!isEnterprise) return label;

   const isYearly = isMonthly === false;
   const price = formatEnterprisePrice(enterpriseTerms, isYearly);
   return price
      ? `the Enterprise plan (${price}/month${isYearly ? `, ${ANNUAL_BILLING_TERMS}` : ""})`
      : null;
}
